import { useEffect, useMemo, useRef, useState } from "react";

import { HttpError, api } from "../api/rest";
import type { PresignStatus } from "../api/types";
import { sameWarmRequest, warmRequest, type WarmRequest } from "../lib/warm";
import { useSettings } from "../store/useSettings";

/**
 * Синхронизация греемого набора с трейдером.
 *
 * Панель — ИСТОЧНИК набора, а не его получатель: суммы придумывает оператор.
 * Хук держит ключ presign:sizes_usd согласованным с настройками вкладки.
 *
 * # Когда отправляется
 *
 * 1. набор изменился — правка пресетов, набранная ручная сумма;
 * 2. трейдер греет ОКРУЖЕНИЕ (`source === "env"`), то есть про наш набор не
 *    знает. Так лечится потеря ключа: чистый Redis, новый стенд, `FLUSHALL`
 *    чужими руками. Без этой ветки расхождение жило бы до следующей правки
 *    пресетов, а оператор всё это время платил бы подписью на горячем пути.
 *
 * # Задержка перед отправкой
 *
 * Поле ввода зовёт setCustomSize на КАЖДОЕ нажатие клавиши: «6», «65», «650».
 * Без задержки трейдер получил бы три набора, на два из которых потратил бы
 * подписи. Полсекунды тишины — это меньше, чем одно нажатие оператора, и заметно
 * меньше периода обхода лесенки (PRESIGN_REFRESH=1s).
 *
 * # Чего хук НЕ делает
 *
 * Не ждёт подтверждения перед показом «~». Знаки на кнопках рисуются по /status,
 * то есть по тому, что трейдер РЕАЛЬНО греет; обещать прогрев по факту отправки
 * запроса значило бы снять «~» с холодной кнопки.
 */

/** Сколько тишины ждать перед отправкой набора. */
const DEBOUNCE_MS = 500;

/** Через сколько повторять после сетевого отказа. */
const RETRY_MS = 10_000;

export interface WarmSync {
  /**
   * Что трейдер греть не будет и почему — дословно из web_api.
   *
   * Показывается оператору: сумма выше RISK_MAX_ORDER_NOTIONAL_USD не станет
   * тёплой никогда, и без объяснения он искал бы причину в прогреве, а не в
   * лимите риска.
   */
  notices: string[];
}

export function useWarmSizes(presign: PresignStatus | null | undefined): WarmSync {
  const settings = useSettings((s) => s.settings);
  const request = useMemo(() => warmRequest(settings), [settings]);
  const [notices, setNotices] = useState<string[]>([]);
  const [retry, setRetry] = useState(0);

  // Что уже отправлено. В ref, а не в состоянии: перерисовка от этого знания
  // ничего не меняет, а лишний рендер на каждое нажатие клавиши — меняет.
  const sent = useRef<WarmRequest | null>(null);

  // Строкой, потому что useEffect сравнивает зависимости по ссылке, а warmRequest
  // отдаёт новый объект на любую правку настроек — включая ту, которая набора не
  // касается (выбор пресета, боевой режим).
  const key = `${request.sizes_usd.join(",")}|${request.presets_usd.join(",")}`;
  const source = presign?.source ?? null;
  // Что греет трейдер прямо сейчас — тоже строкой, и по той же причине: объект
  // /status пересоздаётся на каждом опросе.
  const warmed = presign ? `${presign.sizes_usd.join(",")}|${presign.presets_usd.join(",")}` : "";

  useEffect(() => {
    // null = пред-подпись выключена целиком либо статус ещё не пришёл. В обоих
    // случаях просить прогрев не о чем: web_api на выключенной подсистеме
    // ответит 409, и это правильный отказ, а не то, что стоит вызывать.
    if (source === null) return;
    // Трейдер уже греет ровно наш набор — писать нечего. Так молчит вкладка,
    // открытая рядом с настроенной, и любая вкладка при обычной навигации:
    // запрос на каждый монтаж панели ничего не менял бы, но был бы запросом.
    if (
      presign !== null &&
      presign !== undefined &&
      source === "ui" &&
      sameWarmRequest(request, {
        sizes_usd: presign.sizes_usd,
        presets_usd: presign.presets_usd,
      })
    ) {
      sent.current = request;
      return;
    }
    // Уже отправленное не отправляется повторно: /status отстаёт от записи на
    // один опрос, и без этой памяти набор уходил бы дважды.
    if (sent.current !== null && sameWarmRequest(sent.current, request) && source !== "env") {
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      void api.setPresignSizes(request).then(
        (res) => {
          if (cancelled) return;
          sent.current = request;
          setNotices(res.notices ?? []);
        },
        (err: unknown) => {
          if (cancelled) return;
          // 4xx — отказ по существу: набор не примут и со второй попытки, а
          // повтор превратил бы его в бесконечный цикл. Запоминаем как
          // отправленный, чтобы замолчать до следующей правки.
          if (err instanceof HttpError && err.status < 500) {
            sent.current = request;
            setNotices([`трейдер не принял набор сумм: ${err.message}`]);
            return;
          }
          // Сеть и 5xx проходят сами: пробуем ещё раз, но не чаще RETRY_MS.
          setTimeout(() => {
            if (!cancelled) setRetry((n) => n + 1);
          }, RETRY_MS);
        },
      );
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Зависимости — СТРОКИ, а не объекты: request и presign пересоздаются на
    // каждой правке настроек и на каждом опросе /status, и по ссылке эффект
    // перезапускался бы раз в пять секунд навсегда.
  }, [key, source, warmed, retry]);

  return { notices };
}
